import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { api } from "@/api/client";
import { EmojiPicker } from "@/messaging/EmojiPicker";
import { GifPicker } from "@/messaging/GifPicker";
import { Attachment } from "./Attachment";
import {
  CloseIcon,
  GifIcon,
  PaperclipIcon,
  SendIcon,
  SmileIcon,
} from "./icons";
import type { ChatMessage, SendInput } from "./useMessages";

interface UploadResult {
  url: string;
  kind: "image" | "file";
  size: number | null;
  name: string | null;
}

interface PendingAttachment {
  url: string;
  kind: "image" | "gif" | "file";
  size?: number | null;
  name?: string | null;
}

export interface ComposerHandle {
  addFiles: (files: FileList | File[]) => void;
}

export const Composer = forwardRef<
  ComposerHandle,
  {
    replyTo: ChatMessage | null;
    onCancelReply: () => void;
    onSend: (input: SendInput) => void;
    onTyping: () => void;
  }
>(function Composer({ replyTo, onCancelReply, onSend, onTyping }, ref) {
  const [text, setText] = useState("");
  const [attach, setAttach] = useState<PendingAttachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);

  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const emojiBtnRef = useRef<HTMLButtonElement | null>(null);
  const gifBtnRef = useRef<HTMLButtonElement | null>(null);

  const uploadFile = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api<UploadResult>("/api/messaging/uploads", {
        method: "POST",
        body: fd,
      });
      setAttach({ url: r.url, kind: r.kind, size: r.size, name: r.name });
    } catch {
      setError("Upload failed — check the file type and size.");
    } finally {
      setUploading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    addFiles: (files: FileList | File[]) => {
      const f = Array.from(files)[0];
      if (f) void uploadFile(f);
    },
  }));

  const grow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  const doSend = () => {
    const body = text.trim();
    if (!body && !attach) return;
    onSend({
      body: body || undefined,
      attachmentUrl: attach?.url,
      attachmentKind: attach?.kind,
      replyToId: replyTo?.id ?? null,
    });
    setText("");
    setAttach(null);
    onCancelReply();
    requestAnimationFrame(() => {
      if (taRef.current) taRef.current.style.height = "auto";
    });
  };

  return (
    <div className="border-t border-white/5 bg-ink-900/60 px-2.5 pt-2 pb-2.5">
      {/* reply preview */}
      {replyTo && (
        <div className="mb-1.5 flex items-center gap-2 rounded-lg border-l-2 border-accent-500/60 bg-white/[0.03] px-2.5 py-1.5">
          <ReplyGlyph />
          <div className="min-w-0 flex-1 text-[12px]">
            <div className="font-medium text-slate-300 truncate">
              Reply to {replyTo.sender?.full_name || replyTo.sender?.email || "message"}
            </div>
            <div className="text-slate-500 truncate">
              {replyTo.body || (replyTo.attachment_url ? "Attachment" : "")}
            </div>
          </div>
          <button onClick={onCancelReply} className="icon-btn h-7 w-7" title="Cancel">
            <CloseIcon size={15} />
          </button>
        </div>
      )}

      {/* pending attachment preview */}
      {attach && (
        <div className="mb-1.5 flex items-center gap-2">
          <div className="relative">
            <Attachment
              url={attach.url}
              kind={attach.kind}
              sizeHint={attach.size}
              nameHint={attach.name}
              onOpenImage={() => undefined}
            />
            <button
              onClick={() => setAttach(null)}
              className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-ink-950 border border-white/15 text-slate-300 hover:text-white flex items-center justify-center"
              title="Remove"
            >
              <CloseIcon size={12} />
            </button>
          </div>
        </div>
      )}

      {error && <div className="mb-1.5 text-[11px] text-rose-400 px-1">{error}</div>}

      <div className="flex items-end gap-1.5">
        <input
          ref={fileRef}
          type="file"
          hidden
          accept="image/png,image/jpeg,image/webp,image/gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.ods,.odp,.zip"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void uploadFile(f);
            e.target.value = "";
          }}
        />
        <button
          onClick={() => fileRef.current?.click()}
          className="icon-btn h-9 w-9 flex-shrink-0"
          title="Attach a photo or file"
          disabled={uploading}
        >
          <PaperclipIcon />
        </button>

        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            grow();
            onTyping();
          }}
          onPaste={(e) => {
            const f = Array.from(e.clipboardData.files)[0];
            if (f) {
              e.preventDefault();
              void uploadFile(f);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              doSend();
            }
          }}
          rows={1}
          placeholder="Write a message…"
          className="input flex-1 resize-none max-h-40 min-h-[2.25rem] !py-2 !rounded-2xl scrollbar-thin"
        />

        <button
          ref={gifBtnRef}
          onClick={() => setGifOpen((v) => !v)}
          className="icon-btn h-9 w-9 flex-shrink-0"
          title="GIF"
        >
          <GifIcon />
        </button>
        <button
          ref={emojiBtnRef}
          onClick={() => setEmojiOpen((v) => !v)}
          className="icon-btn h-9 w-9 flex-shrink-0"
          title="Emoji"
        >
          <SmileIcon />
        </button>
        <button
          onClick={doSend}
          disabled={(!text.trim() && !attach) || uploading}
          className="h-9 w-9 flex-shrink-0 rounded-full bg-accent-600 hover:bg-accent-500 disabled:opacity-40 disabled:hover:bg-accent-600 text-white flex items-center justify-center transition active:scale-95"
          title="Send"
        >
          <SendIcon size={17} />
        </button>
      </div>

      <EmojiPicker
        open={emojiOpen}
        triggerRef={emojiBtnRef}
        onClose={() => setEmojiOpen(false)}
        onPick={(emoji) => {
          setText((t) => t + emoji);
          requestAnimationFrame(grow);
        }}
      />
      <GifPicker
        open={gifOpen}
        align="right"
        onClose={() => setGifOpen(false)}
        onPick={(g) => {
          setAttach({ url: g.url, kind: "gif" });
          setGifOpen(false);
        }}
      />
    </div>
  );
});

function ReplyGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-accent-400 flex-shrink-0">
      <path d="M9 8 4 12l5 4M4 12h9a5 5 0 0 1 5 5v1" />
    </svg>
  );
}
